import { z } from 'zod';
import { REGION_VALUES } from '@/shared/utils/regions';
import { env } from '@/shared/lib/env';

export const parcelFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'parcelForm:errors.nameMin')
    .max(120, 'parcelForm:errors.tooLong'),
  // Owner spec (2026-05-31): trek IDs are 5–255 chars, latin/digits/hyphen,
  // case-insensitive — the UI uppercases on input so the regex is satisfied
  // even when the user typed lowercase.
  trek_id: z
    .string()
    .trim()
    .min(5, 'parcelForm:errors.trekIdMin')
    .max(255, 'parcelForm:errors.tooLong')
    .regex(/^[A-Za-z0-9-]+$/u, 'parcelForm:errors.trekIdInvalid'),
  store_name: z.string().trim().max(120, 'parcelForm:errors.tooLong').optional().or(z.literal('')),
  address_name: z
    .string()
    .trim()
    .min(2, 'parcelForm:errors.addressNameRequired')
    .max(120, 'parcelForm:errors.tooLong'),
  region: z.enum(REGION_VALUES, { required_error: 'parcelForm:errors.regionRequired' }),
  city: z
    .string()
    .trim()
    .min(2, 'parcelForm:errors.cityRequired')
    .max(80, 'parcelForm:errors.tooLong'),
  street: z
    .string()
    .trim()
    .min(2, 'parcelForm:errors.streetRequired')
    .max(120, 'parcelForm:errors.tooLong'),
  building: z
    .string()
    .trim()
    .min(1, 'parcelForm:errors.buildingRequired')
    .max(40, 'parcelForm:errors.tooLong'),
  apartment: z.string().trim().max(40, 'parcelForm:errors.tooLong').optional().or(z.literal('')),
  additional_info: z
    .string()
    .trim()
    .max(500, 'parcelForm:errors.tooLong')
    .optional()
    .or(z.literal('')),
  first_name: z
    .string()
    .trim()
    .min(2, 'parcelForm:errors.firstNameRequired')
    .max(60, 'parcelForm:errors.tooLong'),
  last_name: z
    .string()
    .trim()
    .min(2, 'parcelForm:errors.lastNameRequired')
    .max(60, 'parcelForm:errors.tooLong'),
  middle_name: z
    .string()
    .trim()
    .min(2, 'parcelForm:errors.middleNameRequired')
    .max(60, 'parcelForm:errors.tooLong'),
  phone_number: z
    .string()
    .trim()
    .min(7, 'parcelForm:errors.phoneTooShort')
    .max(20, 'parcelForm:errors.tooLong')
    .regex(/^\+?[0-9\s()-]+$/u, 'parcelForm:errors.phoneInvalid'),
  passport_series: z
    .string()
    .min(1, 'parcelForm:errors.passportRequired')
    .regex(/^[A-Z]{2}[0-9]{7}$/, 'parcelForm:errors.passportInvalid'),
  pinfl: z
    .string()
    .trim()
    .length(env.pinflLength, 'parcelForm:errors.pinflLength')
    .regex(/^\d+$/u, 'parcelForm:errors.pinflDigitsOnly'),
});

export type ParcelFormValues = z.infer<typeof parcelFormSchema>;

export const emptyParcelFormValues: ParcelFormValues = {
  name: '',
  trek_id: '',
  store_name: '',
  address_name: '',
  region: 'TASHKENT_CITY',
  city: '',
  street: '',
  building: '',
  apartment: '',
  additional_info: '',
  first_name: '',
  last_name: '',
  middle_name: '',
  phone_number: '',
  passport_series: '',
  pinfl: '',
};
